import { useEffect, useRef, useState } from "react";

import Heading from "../../../shared/ui/Typography/Heading";
import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { fetchClientContacts } from "../api/clientContacts";
import { messageFromError } from "../../../shared/api/drfErrors";

import ClientContactsEditModal from "./ClientContactsEditModal";

// ClientContactsSection — subordinate hydration boundary inside
// /clients/:clientId (CRM-CLIENT-CONTACTS-MATCHING-A1).
//
// Owns:
//   * nested /contacts/ fetch (AbortController + generation guard)
//   * presentation merge of core phone/email + additional channels
//   * additional-contacts edit modal
//
// Core phone/email come from the parent client snapshot and remain
// visible during additional-channel loading/error/refresh.

const TYPE_LABELS = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  viber: "Viber",
  phone: "Телефон",
  email: "Email",
  other: "Інше",
};

const TYPE_ICONS = {
  telegram: "fa-brands fa-telegram",
  whatsapp: "fa-brands fa-whatsapp",
  viber: "fa-brands fa-viber",
  phone: "fa-solid fa-phone",
  email: "fa-solid fa-envelope",
  other: "fa-solid fa-link",
};

const ICON_VARIANTS = {
  telegram: "telegram",
  whatsapp: "whatsapp",
  viber: "viber",
  phone: "phone",
  email: "email",
};

const unwrapListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const buildPresentationRows = (client, additionalContacts) => {
  const rows = [];
  const phone =
    client && typeof client.phone === "string" ? client.phone.trim() : "";
  const email =
    client && typeof client.email === "string" ? client.email.trim() : "";

  if (phone) {
    rows.push({
      key: "core-phone",
      type: "phone",
      value: phone,
      isPrimary: true,
      href: `tel:${phone}`,
      label: TYPE_LABELS.phone,
    });
  }

  if (email) {
    rows.push({
      key: "core-email",
      type: "email",
      value: email,
      isPrimary: true,
      href: `mailto:${email}`,
      label: TYPE_LABELS.email,
    });
  }

  (additionalContacts || []).forEach((row) => {
    if (!row || row.id == null) return;
    const type = row.type || "other";
    const value = row.value || "";
    const href =
      type === "phone"
        ? `tel:${value}`
        : type === "email"
          ? `mailto:${value}`
          : null;
    rows.push({
      key: `extra-${row.id}`,
      type,
      value,
      isPrimary: Boolean(row.is_primary),
      href,
      label: TYPE_LABELS[type] || type || "—",
    });
  });

  return rows;
};

const renderContactRow = (row) => {
  const iconClass = TYPE_ICONS[row.type] || TYPE_ICONS.other;
  const variant = ICON_VARIANTS[row.type];
  const iconClassName = variant
    ? `contact-icon contact-icon--${variant}`
    : "contact-icon";
  const rowClassName = row.isPrimary
    ? "contact-row contact-row--primary"
    : "contact-row";
  return (
    <li key={row.key} className={rowClassName}>
      <span className={iconClassName} aria-hidden="true">
        <i className={iconClass} />
      </span>
      <div className="contact-row__body">
        <span className="contact-row__channel">{row.label}</span>
        {row.href ? (
          <a href={row.href} className="contact-row__value">
            {row.value}
          </a>
        ) : (
          <span className="contact-row__value">{row.value}</span>
        )}
      </div>
      {row.isPrimary ? (
        <span className="primary-flag">
          <i className="fa-solid fa-star" aria-hidden="true" />
          <span>Основний</span>
        </span>
      ) : null}
    </li>
  );
};

function ClientContactsSection({
  client,
  clientId,
  canEdit = true,
  onEditCoreContacts,
}) {
  const [contacts, setContacts] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const requestGenerationRef = useRef(0);
  const activeControllerRef = useRef(null);

  const loadContacts = async ({ mode = "initial" } = {}) => {
    if (clientId == null) return;

    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeControllerRef.current = controller;
    const generation = ++requestGenerationRef.current;

    if (mode === "initial") {
      setLoadState("loading");
      setLoadError(null);
    } else {
      setRefreshing(true);
      setRefreshError(null);
    }

    try {
      const data = await fetchClientContacts(clientId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (generation !== requestGenerationRef.current) return;
      setContacts(unwrapListPayload(data));
      setLoadState("loaded");
      setLoadError(null);
      setRefreshError(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (generation !== requestGenerationRef.current) return;
      const message = messageFromError(err);
      if (mode === "refresh") {
        // Preserve previously loaded additional rows on refresh failure.
        setRefreshError(message);
      } else {
        setLoadError(message);
        setLoadState("error");
      }
    } finally {
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
      if (mode === "refresh" && generation === requestGenerationRef.current) {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    loadContacts({ mode: "initial" });
    return () => {
      requestGenerationRef.current += 1;
      if (activeControllerRef.current) {
        activeControllerRef.current.abort();
        activeControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount on clientId only
  }, [clientId]);

  const handleReload = () => {
    if (refreshing || loadState === "loading") return;
    loadContacts({ mode: "refresh" });
  };

  const handleRetryAdditional = () => {
    loadContacts({ mode: "initial" });
  };

  const presentationRows = buildPresentationRows(client, contacts);
  const coreOnlyRows = buildPresentationRows(client, []);
  const hasCore = coreOnlyRows.length > 0;
  const hasAdditional = contacts.length > 0;
  const showEmptyAdditionalHint =
    loadState === "loaded" && !hasAdditional && hasCore;
  const showFullyEmpty =
    loadState === "loaded" && !hasCore && !hasAdditional;

  return (
    <section
      className="cd-section feature-client-detail__section--contacts"
      aria-labelledby="client-detail-contacts-heading"
    >
      <header className="cd-section__head">
        <Heading
          id="client-detail-contacts-heading"
          level={2}
          variant="section"
          icon="fa-solid fa-address-card"
        >
          Контакти
        </Heading>
        <div className="feature-client-detail__section-actions">
          {canEdit ? (
            <>
              <button
                type="button"
                className="cd-add-btn"
                onClick={() => {
                  if (typeof onEditCoreContacts === "function") {
                    onEditCoreContacts();
                  }
                }}
              >
                Редагувати основні контакти
              </button>
              <button
                type="button"
                className="cd-add-btn"
                onClick={() => setEditOpen(true)}
              >
                {hasAdditional
                  ? "Редагувати додаткові контакти"
                  : "Додати додаткові контакти"}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing || loadState === "loading"}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити контакти"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      {hasCore || hasAdditional ? (
        <ul className="contacts-list" data-testid="contacts-presentation-list">
          {presentationRows.map(renderContactRow)}
        </ul>
      ) : null}

      {loadState === "loading" ? (
        <div
          className="feature-client-detail__section-state"
          data-testid="contacts-additional-loading"
        >
          <Spinner size="md" label="Завантаження додаткових каналів" />
        </div>
      ) : null}

      {loadState === "error" ? (
        <div
          className="feature-client-detail__section-state"
          role="alert"
          data-testid="contacts-additional-error"
        >
          <Text as="p" size="sm" tone="danger">
            {loadError || "Не вдалося завантажити додаткові канали зв'язку."}
          </Text>
          <button
            type="button"
            className="feature-client-detail__action-btn"
            onClick={handleRetryAdditional}
          >
            Повторити
          </button>
        </div>
      ) : null}

      {showEmptyAdditionalHint ? (
        <Text as="p" size="sm" tone="secondary">
          Немає додаткових каналів зв&apos;язку.
        </Text>
      ) : null}

      {showFullyEmpty ? (
        <Text as="p" size="sm" tone="secondary">
          Немає контактів для цього клієнта.
        </Text>
      ) : null}

      <ClientContactsEditModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          loadContacts({ mode: "refresh" });
        }}
        clientId={clientId}
      />
    </section>
  );
}

export default ClientContactsSection;
